import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useMemo } from "react";

const MAX_WHEEL_LETTERS = 7;

function collectLettersFromCoordinates(filledCoordinates) {
    const letters = new Set();
    if (!Array.isArray(filledCoordinates)) return letters;

    filledCoordinates.forEach((item) => {
        if (Array.isArray(item?.positions)) {
            item.positions.forEach((pos) => {
                const ch = (pos?.value || "").trim().toUpperCase();
                if (ch && /[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/.test(ch)) {
                    letters.add(ch);
                }
            });
            return;
        }
        const raw = item?.word?.trim() || item?.text?.trim() || "";
        raw.toUpperCase().split("").forEach((ch) => {
            if (/[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/.test(ch)) {
                letters.add(ch);
            }
        });
    });

    return letters;
}

function wheelLettersFromWordsInUse(wordsInUse) {
    const maxFreq = new Map();
    (wordsInUse || "")
        .split("\n")
        .map((w) => w.trim())
        .filter(Boolean)
        .forEach((word) => {
            const freq = new Map();
            word.toUpperCase().split("").forEach((ch) => {
                if (/[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/.test(ch)) {
                    freq.set(ch, (freq.get(ch) || 0) + 1);
                }
            });
            freq.forEach((count, ch) => {
                maxFreq.set(ch, Math.max(maxFreq.get(ch) || 0, count));
            });
        });
    const tiles = [];
    [...maxFreq.keys()].sort((a, b) => a.localeCompare(b)).forEach((ch) => {
        for (let i = 0; i < maxFreq.get(ch); i += 1) {
            tiles.push(ch);
        }
    });
    return { tiles, distinctCount: maxFreq.size };
}

export default function WordWheelCharacters({ filledCoordinates, wordsInUse, embedded = false, compact = false }) {
    const isCompact = compact || embedded;
    const { tiles, distinctCount } = useMemo(() => {
        const computed = wheelLettersFromWordsInUse(wordsInUse);
        if (computed.tiles.length > 0) {
            return computed;
        }
        const fromCoords = collectLettersFromCoordinates(filledCoordinates);
        const letters = [...fromCoords].sort((a, b) => a.localeCompare(b));
        return { tiles: letters, distinctCount: letters.length };
    }, [filledCoordinates, wordsInUse]);

    const overLimit = distinctCount > MAX_WHEEL_LETTERS;

    const content = (
        <>
            <Typography
                variant={isCompact ? "subtitle2" : "h6"}
                sx={{ fontWeight: "bold", color: "#3BA666", mb: isCompact ? 0.75 : 1 }}
            >
                Characters ({distinctCount} distinct, {tiles.length} wheel)
            </Typography>
            {tiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: isCompact ? "0.8125rem" : undefined }}>
                    No characters yet
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: isCompact ? 0.5 : 1 }}>
                    {tiles.map((letter, index) => (
                        <Chip
                            key={`${letter}-${index}`}
                            label={letter}
                            size={isCompact ? "small" : "medium"}
                            sx={{
                                minWidth: isCompact ? 28 : 36,
                                minHeight: isCompact ? 28 : 36,
                                fontSize: isCompact ? "0.8125rem" : "0.9375rem",
                                fontWeight: 700,
                                bgcolor: "#e8f5e9",
                                color: "#2e7d32",
                                border: "1px solid #a5d6a7",
                                "& .MuiChip-label": { px: isCompact ? 0.75 : 1 },
                            }}
                        />
                    ))}
                </Box>
            )}
            {overLimit && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
                    This puzzle uses {distinctCount} distinct letters; word wheel puzzles should use at most {MAX_WHEEL_LETTERS}.
                </Typography>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            {content}
        </Paper>
    );
}
